import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "lp-calendar__panel" }
const _hoisted_2 = { class: "lp-calendar__header" }
const _hoisted_3 = { class: "lp-calendar__title" }
const _hoisted_4 = { class: "lp-calendar__title-text" }
const _hoisted_5 = { class: "lp-calendar__nav" }
const _hoisted_6 = { class: "lp-calendar__grid" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "lp-calendar__footer" }
const _hoisted_9 = {
  key: 0,
  class: "lp-calendar__time"
}
const _hoisted_10 = { class: "lp-calendar__time-title" }
const _hoisted_11 = { class: "lp-calendar__time-columns" }
const _hoisted_12 = { class: "lp-calendar__time-column" }
const _hoisted_13 = { class: "lp-calendar__time-label" }
const _hoisted_14 = { class: "lp-calendar__time-scroll" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "lp-calendar__time-column" }
const _hoisted_17 = { class: "lp-calendar__time-label" }
const _hoisted_18 = { class: "lp-calendar__time-scroll" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "lp-calendar__time-column" }
const _hoisted_21 = { class: "lp-calendar__time-label" }
const _hoisted_22 = { class: "lp-calendar__time-scroll" }
const _hoisted_23 = ["onClick"]

import { computed, ref, watch } from 'vue';
import { i18n } from '@/i18n';
import { CalendarDay, useCalendar } from '@/corePagesModule/composables/useCalendar';
import Mdi from './Mdi.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'Calendar',
  props: {
    modelValue: { default: null },
    firstDayOfWeek: { default: 0 },
    hideTime: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "change-month"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;

const props = __props;

const emit = __emit;

const selectedDate = ref<Date | null>(props.modelValue ? new Date(props.modelValue) : null);

/**
 * Dateをタイムスタンプに変換する。
 * @param value - 変換対象
 */
const toTimestamp = (value?: Date | null): number | null => {
  return value ? value.getTime() : null;
};

const { weeks, headerLabel, displayYearMonth, moveMonth, selectDate, clearSelection, goToday } = useCalendar({
  selectedDate,
  firstDayOfWeek: props.firstDayOfWeek,
});

watch(
  () => props.modelValue,
  (value) => {
    selectedDate.value = value ? new Date(value) : null;
  }
);

watch(selectedDate, (value) => {
  // 同じタイムスタンプの場合は親へ再送出しない
  if (toTimestamp(value) === toTimestamp(props.modelValue ?? null)) return;
  emit('update:modelValue', value ? new Date(value) : null);
});

watch(
  displayYearMonth,
  (value) => {
    emit('change-month', { year: value.year, month: value.month });
  },
  { immediate: true }
);

const dayNames = computed(() => [t('曜日:日'), t('曜日:月'), t('曜日:火'), t('曜日:水'), t('曜日:木'), t('曜日:金'), t('曜日:土')]);
const todayLabel = computed(() => t('今日'));
const deleteLabel = computed(() => t('削除'));
const timeTitle = computed(() => t('時間'));
const hourLabel = computed(() => t('時'));
const minuteLabel = computed(() => t('分'));
const secondLabel = computed(() => t('秒'));

const hours = computed(() => Array.from({ length: 24 }, (_, i) => i));
const minutes = computed(() => Array.from({ length: 60 }, (_, i) => i));
const seconds = computed(() => Array.from({ length: 60 }, (_, i) => i));

const selectedHour = computed(() => (selectedDate.value ? selectedDate.value.getHours() : 0));
const selectedMinute = computed(() => (selectedDate.value ? selectedDate.value.getMinutes() : 0));
const selectedSecond = computed(() => (selectedDate.value ? selectedDate.value.getSeconds() : 0));

/**
 * 日付選択時のハンドラ。
 * @param day - 押下された日付情報
 */
const handleSelectDay = (day: CalendarDay) => {
  selectDate(day.date);
};

/**
 * 月移動ボタンのハンドラ。
 * @param diff - 増減させる月数
 */
const handleMoveMonth = (diff: number) => {
  moveMonth(diff);
};

/**
 * 選択解除ボタンのハンドラ。
 */
const handleClear = () => {
  clearSelection();
};

/**
 * 今日へ移動するハンドラ。
 */
const handleToday = () => {
  goToday();
};

/**
 * 選択中の日付に時刻を設定する。
 * @param hour - 設定する時
 * @param minute - 設定する分
 * @param second - 設定する秒
 */
const applyTime = (hour: number, minute: number, second: number) => {
  const base = selectedDate.value ? new Date(selectedDate.value) : new Date();
  base.setHours(hour, minute, second, 0);
  selectedDate.value = base;
};

/**
 * 時の選択時に時刻を更新する。
 * @param hour - 設定する時
 */
const handleSelectHour = (hour: number) => {
  applyTime(hour, selectedMinute.value, selectedSecond.value);
};

/**
 * 分の選択時に時刻を更新する。
 * @param minute - 設定する分
 */
const handleSelectMinute = (minute: number) => {
  applyTime(selectedHour.value, minute, selectedSecond.value);
};

/**
 * 秒の選択時に時刻を更新する。
 * @param second - 設定する秒
 */
const handleSelectSecond = (second: number) => {
  applyTime(selectedHour.value, selectedMinute.value, second);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["lp-calendar", { 'lp-calendar--date-only': _ctx.hideTime }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(headerLabel)), 1)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("button", {
            class: "lp-calendar__nav-button",
            type: "button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (handleMoveMonth(-1)))
          }, [
            _createVNode(Mdi, {
              icon: "arrow_upward",
              "aria-label": "prev-month"
            })
          ]),
          _createElementVNode("button", {
            class: "lp-calendar__nav-button",
            type: "button",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (handleMoveMonth(1)))
          }, [
            _createVNode(Mdi, {
              icon: "arrow_downward",
              "aria-label": "next-month"
            })
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dayNames.value, (dayName) => {
          return (_openBlock(), _createElementBlock("div", {
            key: dayName,
            class: _normalizeClass(["lp-calendar__weekday", {
            'lp-calendar__weekday--sunday': dayName === _unref(t)('曜日:日'),
            'lp-calendar__weekday--saturday': dayName === _unref(t)('曜日:土'),
          }])
          }, _toDisplayString(dayName), 3))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(weeks), (week) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: `${week.weekNumber}-${week.days[0].date.toISOString()}`
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(week.days, (day) => {
              return (_openBlock(), _createElementBlock("button", {
                key: day.date.toISOString(),
                type: "button",
                class: _normalizeClass(["lp-calendar__day", {
              'lp-calendar__day--other': !day.isCurrentMonth,
              'lp-calendar__day--sunday': day.isCurrentMonth && day.date.getDay() === 0,
              'lp-calendar__day--saturday': day.isCurrentMonth && day.date.getDay() === 6,
              'lp-calendar__day--selected': day.isSelected,
              'lp-calendar__day--today': day.isToday && !day.isSelected,
            }]),
                onClick: ($event: any) => (handleSelectDay(day))
              }, _toDisplayString(day.date.getDate()), 11, _hoisted_7))
            }), 128))
          ], 64))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("button", {
          class: "lp-calendar__link-button",
          type: "button",
          onClick: handleClear
        }, _toDisplayString(deleteLabel.value), 1),
        _createElementVNode("button", {
          class: "lp-calendar__link-button lp-calendar__link-button--today",
          type: "button",
          onClick: handleToday
        }, _toDisplayString(todayLabel.value), 1)
      ])
    ]),
    (!_ctx.hideTime)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, _toDisplayString(timeTitle.value), 1),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, _toDisplayString(hourLabel.value), 1),
              _createElementVNode("div", _hoisted_14, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hours.value, (hour) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: `hour-${hour}`,
                    type: "button",
                    class: _normalizeClass(["lp-calendar__time-cell", { 'lp-calendar__time-cell--active': hour === selectedHour.value }]),
                    onClick: ($event: any) => (handleSelectHour(hour))
                  }, _toDisplayString(hour.toString().padStart(2, '0')), 11, _hoisted_15))
                }), 128))
              ])
            ]),
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("div", _hoisted_17, _toDisplayString(minuteLabel.value), 1),
              _createElementVNode("div", _hoisted_18, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(minutes.value, (minute) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: `minute-${minute}`,
                    type: "button",
                    class: _normalizeClass(["lp-calendar__time-cell", { 'lp-calendar__time-cell--active': minute === selectedMinute.value }]),
                    onClick: ($event: any) => (handleSelectMinute(minute))
                  }, _toDisplayString(minute.toString().padStart(2, '0')), 11, _hoisted_19))
                }), 128))
              ])
            ]),
            _createElementVNode("div", _hoisted_20, [
              _createElementVNode("div", _hoisted_21, _toDisplayString(secondLabel.value), 1),
              _createElementVNode("div", _hoisted_22, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(seconds.value, (second) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: `second-${second}`,
                    type: "button",
                    class: _normalizeClass(["lp-calendar__time-cell", { 'lp-calendar__time-cell--active': second === selectedSecond.value }]),
                    onClick: ($event: any) => (handleSelectSecond(second))
                  }, _toDisplayString(second.toString().padStart(2, '0')), 11, _hoisted_23))
                }), 128))
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})